import { useState } from "react";
import { Check, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "@/providers/language-provider";

const localeText = {
  en: {
    BackgroundColorTitle: "My Message BG Color",
    BackgroundColorDesc: "This color appears to the other person.",
    Preview: "Preview",
    PreviewMessage: "Sphinx of black quartz, judge my vow.",
  },
  ko: {
    BackgroundColorTitle: "내 메시지 배경 색상",
    BackgroundColorDesc: "상대방에게 이 색상으로 보입니다.",
    Preview: "미리보기",
    PreviewMessage: "동해물과 백두산이 마르고 닳도록",
  },
} as const;

interface PresetColor {
  name: string;
  hex: string;
}

const PRESET_COLORS: PresetColor[] = [
  // 1. Blue & Navy (신뢰와 지성)
  { name: "Ink Blue", hex: "#141B2F" }, // 매우 어두운 네이비 (slate-950)
  { name: "Royal", hex: "#1E40AF" }, // 선명한 블루 (blue-800)
  { name: "Cobalt", hex: "#2563EB" }, // 밝은 코발트 (blue-600)
  { name: "Cerulean", hex: "#0369A1" }, // 차분한 바다색 (sky-700)
  { name: "Deep Cyan", hex: "#0891B2" }, // 진한 청록 (cyan-600)

  // 2. Green & Teal (평온과 자연)
  { name: "Evergreen", hex: "#064E3B" }, // 짙은 상록수 (emerald-950)
  { name: "Forest", hex: "#166534" }, // 묵직한 숲색 (green-800)
  { name: "Viridian", hex: "#0D9488" }, // 선명한 청록 (teal-600)
  { name: "Deep Moss", hex: "#3F6212" }, // 이끼색 (lime-800)
  { name: "Jade", hex: "#047857" }, // 옥색 (emerald-700)

  // 3. Red & Orange (열정과 에너지)
  { name: "Maroon", hex: "#7F1D1D" }, // 검붉은색 (red-900)
  { name: "Scarlet", hex: "#DC2626" }, // 강렬한 레드 (red-600)
  { name: "Burnt Clay", hex: "#92400E" }, // 구운 점토 (amber-800)
  { name: "Rust", hex: "#B45309" }, // 녹슨 오렌지 (amber-700)
  { name: "Persimmon", hex: "#EA580C" }, // 감색 (orange-600)

  // 4. Pink & Purple (우아함과 신비)
  { name: "Eggplant", hex: "#581C87" }, // 가지색 (purple-900)
  { name: "Amethyst", hex: "#7E22CE" }, // 보라 (purple-700)
  { name: "Grape", hex: "#6D28D9" }, // 진보라 (violet-700)
  { name: "Wine", hex: "#881337" }, // 와인색 (rose-900)
  { name: "Magenta", hex: "#BE123C" }, // 진분홍 (rose-700)

  // 5. Brown & Earth (안정과 따뜻함)
  { name: "Espresso", hex: "#422006" }, // 커피색 (yellow-950)
  { name: "Wood", hex: "#713F12" }, // 나무색 (yellow-900)
  { name: "Cocoa", hex: "#78350F" }, // 코코아 (amber-900)
  { name: "Sepia", hex: "#7C2D12" }, // 세피아 (orange-900)
  { name: "Tan", hex: "#854D0E" }, // 황토색 (amber-800)

  // 6. Mono & Dark (세련된 미니멀리즘)
  { name: "Obsidian", hex: "#1A1A1A" }, // 완전한 블랙
  { name: "Coal", hex: "#262626" }, // 숯색 (neutral-800)
  { name: "Steel", hex: "#475569" }, // 강철색 (slate-600)
  { name: "Storm", hex: "#1E293B" }, // 폭풍우 색 (slate-800)
  { name: "Gunmetal", hex: "#334155" }, // 금속 회색 (slate-700)
];

interface ChatSettingsProps {
  color?: string;
  onColorChange: (color: string) => void;
}

export function ChatColorPicker({ color, onColorChange }: ChatSettingsProps) {
  const { language } = useTranslation();
  const text =
    localeText[language as keyof typeof localeText] ?? localeText["en"];
  const [selectedColor, setSelectedColor] = useState<string>(
    color ?? "#3B82F6",
  );

  const handleSelect = (hex: string) => {
    setSelectedColor(hex);
    onColorChange(hex);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <span className="text-sm font-medium">
            {text.BackgroundColorTitle}
          </span>
          <p className="text-xs text-muted-foreground">
            {text.BackgroundColorDesc}
          </p>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-[80px] justify-start gap-2 shadow-sm"
              style={{ backgroundColor: selectedColor }}
            />
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="end"
            sideOffset={10}
            className="w-[300px] p-4 shadow-2xl z-50"
          >
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color.hex}
                  onClick={() => handleSelect(color.hex)}
                  className="group relative flex h-12 w-full items-center justify-center rounded-lg transition-all hover:scale-110 active:scale-95 shadow-sm"
                  style={{ backgroundColor: color.hex }}
                >
                  {selectedColor === color.hex && (
                    <Check className="w-5 h-5 text-white drop-shadow-md animate-in zoom-in duration-200" />
                  )}
                  {/* 호버 시 색상 이름 툴팁 (선택 사항) */}
                  <span className="sr-only">{color.name}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Separator />

      {/* 채팅 미리보기 섹션 */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-400 tracking-wider">
          <MessageCircle className="w-3.5 h-3.5" />
          {text.Preview}
        </div>
        <div
          style={{ backgroundColor: selectedColor }}
          className="flex rounded-xl text-white p-3 text-sm shadow-md"
        >
          {text.PreviewMessage}
        </div>
      </div>
    </div>
  );
}
